import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { useHistory, useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  IonButton,
  IonText,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import { isEmpty } from 'lodash'

import { LoadingModal, Modal } from 'components'
import { StatusTile, WorkOrderPreview } from '../components'
import { WorkOrderCheckInForm, WorkOrderCheckoutForm } from '../forms'
import WorkOrderSearchNoResults from './WorkOrderSearchNoResults'
import WorkOrderCustomerAppInstructions from './WorkOrderCustomerAppInstructions'
import { splitCheckOutFormValues } from '../checkOutFormValues'
import {
  buildCheckoutInitialValues,
  getCheckoutPhotoFieldNames,
  getCheckoutQuestionResponseFields,
} from '../checkOutFormFields'
import { selectors } from '../reducer'
import * as actions from '../actions'
import * as apiActions from 'api-actions'

import {
  CheckOutForm,
  ERROR_CODE,
  ORDER_STATUS,
  ORDER_SUB_STATUS,
  trip,
  workOrder,
  GEOLOCATION_STATUS,
} from 'types'
import {
  getApiErrorCode,
  isValidationError,
  prepareQuestionResponses,
  useSubmitMessages,
  runGeolocationCheck,
  checkIfRootCauseNeglectOrDefect,
} from 'utils'

const propTypes = {
  checkingOut: PropTypes.bool,
  clearWorkOrder: PropTypes.func.isRequired,
  createTrip: PropTypes.func.isRequired,
  currentCheckOutForm: CheckOutForm,
  currentTrip: trip,
  fetchWorkOrder: PropTypes.func.isRequired,
  startCheckOut: PropTypes.func.isRequired,
  stopCheckOut: PropTypes.func.isRequired,
  updateTrip: PropTypes.func.isRequired,
  workOrder: workOrder,
}
const defaultProps = {}

function WorkOrderShow({
  checkingOut,
  clearWorkOrder,
  createTrip,
  currentCheckOutForm,
  currentTrip,
  fetchWorkOrder,
  startCheckOut,
  stopCheckOut,
  updateTrip,
  workOrder,
}) {
  const { workOrderId } = useParams()

  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState({})
  const selectImage = (image) => {
    setIsImageModalOpen(true)
    setSelectedImage(image)
  }

  // Fetch on mount so every arrival loads data — including the history.replace return
  // from the questionnaire, which does not reliably fire Ionic's view events. Clear
  // only the work order on the way out (currentTrip / currentQuestionnaire survive for
  // the in-flight questionnaire).
  useEffect(() => {
    fetchWorkOrder(workOrderId)
    return () => {
      clearWorkOrder()
    }
  }, [clearWorkOrder, fetchWorkOrder, workOrderId])

  // Ionic also keeps this page mounted and can re-show it without remounting. Refetch
  // on visible entry too, so a revisit can't render another screen's cached payload
  // (manager detail requests share this same work-order slice).
  useIonViewWillEnter(() => {
    fetchWorkOrder(workOrderId)
  }, [fetchWorkOrder, workOrderId])

  useIonViewDidLeave(() => {
    stopCheckOut()
  })

  if (workOrder?.notFound) return <WorkOrderSearchNoResults />
  if (workOrder?.isForCustomerApp) return <WorkOrderCustomerAppInstructions />
  if (!workOrder) return <LoadingModal />

  return (
    <section>
      <Modal
        isOpen={isImageModalOpen}
        dismissModal={() => setIsImageModalOpen(false)}
        header={selectedImage?.filename}
      >
        <img
          alt={selectedImage?.filename}
          src={`${selectedImage?.url}`}
          className="work-order-image-full"
        />
      </Modal>

      <header>
        <h2>{workOrder.location?.name}</h2>
        <div className="flex">
          <IonText
            color="primary"
            className="body-small mr-auto flex align-center"
          >
            <span>{workOrder.name}</span>
          </IonText>
          <StatusTile status={workOrder.orderStatus} />
        </div>
      </header>
      <hr />
      <WorkOrderPreview {...{ selectImage, workOrder }} />
      <NextWorkOrderStep
        {...{
          checkingOut,
          createTrip,
          currentCheckOutForm,
          currentTrip,
          fetchWorkOrder,
          startCheckOut,
          stopCheckOut,
          updateTrip,
          workOrder,
        }}
      />
    </section>
  )
}

WorkOrderShow.propTypes = propTypes
WorkOrderShow.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    workOrder: selectors.workOrder(state),
    currentTrip: selectors.currentTrip(state),
    currentCheckOutForm: selectors.currentCheckOutForm(state),
    checkingOut: selectors.checkingOut(state),
  }
}

const mapDispatchToProps = {
  fetchWorkOrder: apiActions.fetchWorkOrder,
  createTrip: apiActions.createTrip,
  updateTrip: apiActions.updateTrip,
  clearWorkOrder: actions.clearWorkOrder,
  startCheckOut: actions.startCheckOut,
  stopCheckOut: actions.stopCheckOut,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  WorkOrderShow
)

function NextWorkOrderStep({
  checkingOut,
  createTrip,
  currentCheckOutForm,
  currentTrip,
  fetchWorkOrder,
  startCheckOut,
  stopCheckOut,
  updateTrip,
  workOrder,
}) {
  const history = useHistory()
  const { displaySubmitFailure, displaySubmitSuccess, displaySubmitWarning } =
    useSubmitMessages()
  const { orderStatus, orderSubStatus } = workOrder

  // The backend is the single source of truth for whether a pre-work questionnaire
  // is still pending. It already accounts for installations, a missing trip, and a
  // completed/blank questionnaire, so the client just trusts the flag.
  const hasQuestionnaireToComplete = workOrder.requiresQuestionnaireCompletion

  const checkoutFields = currentCheckOutForm?.fields ?? []
  const questionResponseFields =
    getCheckoutQuestionResponseFields(checkoutFields)
  const checkoutPhotoFieldNames = getCheckoutPhotoFieldNames(checkoutFields)

  const requireGeolocationCheck = workOrder.shouldRunGeolocationCheck

  const handleCheckoutFailure = (errors, dispatch, submitError) => {
    displaySubmitFailure(errors, dispatch, submitError)
    if (!isValidationError(errors, submitError)) {
      const errorCode = getApiErrorCode(errors)
      if (errorCode === ERROR_CODE.TRIP_TIMED_OUT) {
        stopCheckOut()
        fetchWorkOrder(workOrder.id)
      }
    }
  }

  const handleCheckInSubmit = () => {
    const createTripWithCoordinates = async (coordinates = []) => {
      const workOrderResponse = await createTrip(
        {
          workOrderId: workOrder.id,
        },
        coordinates
      )
      return workOrderResponse
    }
    return runGeolocationCheck(
      createTripWithCoordinates,
      requireGeolocationCheck
    )
  }

  const handleCheckoutSubmit = async (formValues) => {
    const { tripValues, questionValues } = splitCheckOutFormValues(formValues, {
      photoFieldNames: checkoutPhotoFieldNames,
      questionFieldNames: questionResponseFields.map(
        ({ fieldName }) => fieldName
      ),
      keepAbuseNeglectClaims: checkIfRootCauseNeglectOrDefect(
        formValues.rootCause
      ),
    })

    const questionResponses = isEmpty(questionResponseFields)
      ? []
      : prepareQuestionResponses(
          questionValues,
          questionResponseFields,
          currentTrip
        )

    const updateTripWithCoordinates = async (coordinates = []) => {
      const workOrderResponse = await updateTrip(
        currentTrip.id,
        tripValues,
        coordinates,
        questionResponses
      )
      return workOrderResponse
    }
    return runGeolocationCheck(
      updateTripWithCoordinates,
      requireGeolocationCheck
    )
  }

  // If a work order has timed out, force the user to check in again. Otherwise,
  // require the user to check in if the work order is in any state other than
  // "Tech on Site"
  if (
    orderSubStatus === ORDER_SUB_STATUS.IVR_TIMEOUT ||
    orderStatus !== ORDER_STATUS.TECH_ON_SITE
  ) {
    return (
      <WorkOrderCheckInForm
        requireGeolocationCheck={requireGeolocationCheck}
        initialValues={{}}
        onSubmit={async () => {
          try {
            const workOrderResponse = await handleCheckInSubmit()
            // For some reason, when we get the response from the API,
            // The WorkOrder's Trip field is always 1 trip behind. It is not updated in the same way the fields directly on the WorkOrder are.
            // So we have to cache the value of the latest trip's geolocation status
            // on the WorkOrder with geolocationCheckinMostRecentStatus
            const showGeofenceModal =
              workOrderResponse &&
              workOrderResponse.geolocationCheckinMostRecentStatus ===
                GEOLOCATION_STATUS.OUTSIDE &&
              workOrder.shouldRunGeolocationCheck
            if (showGeofenceModal) {
              await displaySubmitWarning(
                "FYI this checkin has been marked as being 'Outside' of the designated 1 mile geofence radius for this location."
              )
            }
            // checkingOut lives in the same slice as the work order and is not cleared
            // with it. A prior "Continue to Checkout" can still be true after this
            // check-in (same mounted page, or didLeave never ran). Reset so the tech
            // has to click through again.
            stopCheckOut()
            // The backend tells us whether a pre-work questionnaire is still pending.
            // Only then do we route to the questionnaire step; installations and work
            // orders with nothing left to ask stay here and continue straight to checkout.
            if (workOrderResponse?.requiresQuestionnaireCompletion) {
              history.replace(`/work_orders/questionnaire/${workOrder.id}`)
            }
          } catch (error) {
            const errors = error.errors
            displaySubmitFailure(errors, null, error)
          }
        }}
      />
    )
  } else {
    if (checkingOut) {
      return (
        <WorkOrderCheckoutForm
          fields={checkoutFields}
          initialValues={buildCheckoutInitialValues(
            checkoutFields,
            currentTrip
          )}
          onSubmit={async (formValues) => {
            try {
              const workOrderResponse = await handleCheckoutSubmit(formValues)
              const showGeofenceModal =
                workOrderResponse?.trips?.at(-1)?.checkoutGeofenceStatus ===
                  GEOLOCATION_STATUS.OUTSIDE &&
                workOrder.shouldRunGeolocationCheck
              if (showGeofenceModal) {
                await displaySubmitWarning(
                  "You have been checked out! FYI this checkout has been marked as being 'Outside' of the designated 1 mile geofence radius for this location."
                )
              } else {
                await displaySubmitSuccess('You have been checked out!')
              }
              history.replace('/')
            } catch (error) {
              const errors = error.errors
              handleCheckoutFailure(errors, null, error)
            }
          }}
        />
      )
    } else if (!hasQuestionnaireToComplete) {
      return (
        <div className="w-100p">
          <IonButton
            expand="full"
            onClick={() => startCheckOut()}
            shape="round"
            size="large"
          >
            Continue to Checkout
          </IonButton>
        </div>
      )
    } else {
      return (
        <div className="w-100p">
          <IonButton
            expand="full"
            onClick={() =>
              history.replace(`/work_orders/questionnaire/${workOrder.id}`)
            }
            shape="round"
            size="large"
          >
            Complete Questionnaire
          </IonButton>
        </div>
      )
    }
  }
}
